// Groups service - handles group operations in Firestore (unencrypted)
import { db } from '../firebase';
import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  query, 
  where, 
  orderBy, 
  limit,
  getDocs,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  arrayUnion,
  arrayRemove,
  increment,
  runTransaction
} from 'firebase/firestore';
import { recordActivity, ACTIVITY_TYPES } from './activityService';

/**
 * Normalize wallet address
 */
const normalize = (addr) => {
  if (!addr || typeof addr !== 'string') {
    throw new Error('Invalid address: must be a non-empty string');
  }
  let normalized = addr.toLowerCase().trim();
  if (!normalized.startsWith('0x')) {
    normalized = '0x' + normalized;
  }
  return normalized;
};

/**
 * Create a new group
 * @param {string} creatorAddress - Wallet address of group creator
 * @param {Object} groupData - Group data (name, description, tags, privacy, etc.)
 * @returns {Promise<string>} Group ID
 */
export const createGroup = async (creatorAddress, groupData) => {
  if (!creatorAddress || !groupData || !groupData.name) {
    throw new Error('Creator address and group name are required');
  }

  // Input validation to prevent DoS attacks
  const MAX_NAME_LENGTH = 100;
  const MAX_DESCRIPTION_LENGTH = 5000;
  const MAX_TAGS = 50;
  const MAX_RULES = 20;
  const MAX_RULE_LENGTH = 500;

  const name = groupData.name.trim();
  if (name.length === 0) {
    throw new Error('Group name cannot be empty');
  }
  if (name.length > MAX_NAME_LENGTH) {
    throw new Error(`Group name must be no more than ${MAX_NAME_LENGTH} characters`);
  }

  const description = (groupData.description || '').trim();
  if (description.length > MAX_DESCRIPTION_LENGTH) {
    throw new Error(`Group description must be no more than ${MAX_DESCRIPTION_LENGTH} characters`);
  }

  const tags = groupData.tags || [];
  if (!Array.isArray(tags)) {
    throw new Error('Tags must be an array');
  }
  if (tags.length > MAX_TAGS) {
    throw new Error(`Maximum ${MAX_TAGS} tags allowed`);
  }

  const rules = groupData.rules || [];
  if (!Array.isArray(rules)) {
    throw new Error('Rules must be an array');
  }
  if (rules.length > MAX_RULES) {
    throw new Error(`Maximum ${MAX_RULES} rules allowed`);
  }
  for (const rule of rules) {
    if (typeof rule === 'string' && rule.length > MAX_RULE_LENGTH) {
      throw new Error(`Each rule must be no more than ${MAX_RULE_LENGTH} characters`);
    }
  }

  const creator = normalize(creatorAddress);
  const groupId = `group_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`; // Fixed: use substring instead of deprecated substr

  const groupDoc = {
    groupId,
    name: name,
    description: description,
    tags: tags,
    privacy: groupData.privacy || 'public', // 'public', 'private', 'invite-only'
    creator: creator,
    members: [creator],
    moderators: [creator],
    memberCount: 1,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    coverImage: groupData.coverImage || null,
    location: groupData.location || null, // Optional location for groups
    rules: rules
  };

  const groupsRef = collection(db, 'groups');
  const groupRef = doc(groupsRef, groupId);
  await setDoc(groupRef, groupDoc);

  // Add user's group membership
  await addUserGroupMembership(creator, groupId, 'member');

  // Record activity
  try {
    await recordActivity(ACTIVITY_TYPES.GROUP_JOINED, creator, {
      groupId,
      groupName: name
    });
  } catch (error) {
    console.warn('Error recording group join activity:', error);
  }

  return groupId;
};

/**
 * Get a group by ID
 * @param {string} groupId - Group ID
 * @returns {Promise<Object|null>} Group data or null
 */
export const getGroup = async (groupId) => {
  if (!groupId) return null;

  try {
    const groupsRef = collection(db, 'groups');
    const groupRef = doc(groupsRef, groupId);
    const groupDoc = await getDoc(groupRef);

    if (groupDoc.exists()) {
      return { id: groupDoc.id, ...groupDoc.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting group:', error);
    throw error;
  }
};

/**
 * Update group information
 * @param {string} groupId - Group ID
 * @param {string} updaterAddress - Wallet address of updater (must be creator or moderator)
 * @param {Object} updates - Fields to update
 * @returns {Promise<void>}
 */
export const updateGroup = async (groupId, updaterAddress, updates) => {
  if (!groupId || !updaterAddress) {
    throw new Error('Group ID and updater address are required');
  }

  const updater = normalize(updaterAddress);
  const group = await getGroup(groupId);

  if (!group) {
    throw new Error('Group not found');
  }

  // Check permissions
  const isCreator = group.creator === updater;
  const isModerator = (group.moderators || []).includes(updater);

  if (!isCreator && !isModerator) {
    throw new Error('Only creators and moderators can update groups');
  }

  const groupsRef = collection(db, 'groups');
  const groupRef = doc(groupsRef, groupId);

  // Prepare allowed updates
  const allowedUpdates = {};
  if (updates.name !== undefined) allowedUpdates.name = updates.name.trim();
  if (updates.description !== undefined) allowedUpdates.description = updates.description;
  if (updates.tags !== undefined) allowedUpdates.tags = updates.tags;
  if (updates.privacy !== undefined) allowedUpdates.privacy = updates.privacy;
  if (updates.coverImage !== undefined) allowedUpdates.coverImage = updates.coverImage;
  if (updates.location !== undefined) allowedUpdates.location = updates.location;
  if (updates.rules !== undefined) allowedUpdates.rules = updates.rules;
  
  allowedUpdates.updatedAt = serverTimestamp();

  await updateDoc(groupRef, allowedUpdates);
};

/**
 * Delete a group (only creator can delete)
 * @param {string} groupId - Group ID
 * @param {string} creatorAddress - Wallet address of creator
 * @returns {Promise<void>}
 */
export const deleteGroup = async (groupId, creatorAddress) => {
  if (!groupId || !creatorAddress) {
    throw new Error('Group ID and creator address are required');
  }

  const creator = normalize(creatorAddress);
  const group = await getGroup(groupId);

  if (!group) {
    throw new Error('Group not found');
  }

  if (group.creator !== creator) {
    throw new Error('Only the creator can delete a group');
  }

  const groupsRef = collection(db, 'groups');
  const groupRef = doc(groupsRef, groupId);
  await deleteDoc(groupRef);

  // Clean up memberships (optional - can be done via Cloud Function)
  // For now, we'll leave memberships but they'll be orphaned
};

/**
 * Join a group
 * @param {string} groupId - Group ID
 * @param {string} userAddress - Wallet address of user joining
 * @returns {Promise<void>}
 */
export const joinGroup = async (groupId, userAddress) => {
  if (!groupId || !userAddress) {
    throw new Error('Group ID and user address are required');
  }

  const user = normalize(userAddress);
  const group = await getGroup(groupId);

  if (!group) {
    throw new Error('Group not found');
  }

  // Check privacy settings
  if (group.privacy === 'private') {
    throw new Error('This group is private. You need an invitation to join.');
  }

  // Check if already a member
  if (group.members && group.members.includes(user)) {
    return; // Already a member
  }

  const groupsRef = collection(db, 'groups');
  const groupRef = doc(groupsRef, groupId);

  await runTransaction(db, async (transaction) => {
    const groupDoc = await transaction.get(groupRef);
    if (!groupDoc.exists()) {
      throw new Error('Group not found');
    }

    const currentData = groupDoc.data();
    if (currentData.members && currentData.members.includes(user)) {
      return; // Already a member
    }

    transaction.update(groupRef, {
      members: arrayUnion(user),
      memberCount: increment(1),
      updatedAt: serverTimestamp()
    });
  });

  // Add user's group membership
  await addUserGroupMembership(user, groupId, 'member');

  // Record activity
  try {
    const group = await getGroup(groupId);
    await recordActivity(ACTIVITY_TYPES.GROUP_JOINED, user, {
      groupId,
      groupName: group?.name || 'Unknown Group'
    });
  } catch (error) {
    console.warn('Error recording group join activity:', error);
  }
};

/**
 * Leave a group
 * @param {string} groupId - Group ID
 * @param {string} userAddress - Wallet address of user leaving
 * @returns {Promise<void>}
 */
export const leaveGroup = async (groupId, userAddress) => {
  if (!groupId || !userAddress) {
    throw new Error('Group ID and user address are required');
  }

  const user = normalize(userAddress);
  const group = await getGroup(groupId);

  if (!group) {
    throw new Error('Group not found');
  }

  // Creator cannot leave (must delete group or transfer ownership)
  if (group.creator === user) {
    throw new Error('Creators cannot leave their groups. Delete the group or transfer ownership instead.');
  }

  const groupsRef = collection(db, 'groups');
  const groupRef = doc(groupsRef, groupId);

  await runTransaction(db, async (transaction) => {
    const groupDoc = await transaction.get(groupRef);
    if (!groupDoc.exists()) {
      throw new Error('Group not found');
    }

    const currentData = groupDoc.data();
    
    // Remove from members and moderators
    const updates = {
      updatedAt: serverTimestamp()
    };

    if (currentData.members && currentData.members.includes(user)) {
      updates.members = arrayRemove(user);
      updates.memberCount = increment(-1);
    }

    if (currentData.moderators && currentData.moderators.includes(user)) {
      updates.moderators = arrayRemove(user);
    }

    transaction.update(groupRef, updates);
  });

  // Remove user's group membership
  await removeUserGroupMembership(user, groupId);
};

/**
 * Invite user to group (for private/invite-only groups)
 * @param {string} groupId - Group ID
 * @param {string} inviterAddress - Wallet address of inviter
 * @param {string} inviteeAddress - Wallet address of invitee
 * @returns {Promise<void>}
 */
export const inviteToGroup = async (groupId, inviterAddress, inviteeAddress) => {
  if (!groupId || !inviterAddress || !inviteeAddress) {
    throw new Error('Group ID, inviter, and invitee addresses are required');
  }

  const inviter = normalize(inviterAddress);
  const invitee = normalize(inviteeAddress);
  const group = await getGroup(groupId);

  if (!group) {
    throw new Error('Group not found');
  }

  // Check permissions
  const isCreator = group.creator === inviter;
  const isModerator = (group.moderators || []).includes(inviter);
  const isMember = (group.members || []).includes(inviter);

  if (!isCreator && !isModerator && group.privacy !== 'invite-only') {
    throw new Error('Only creators and moderators can invite users');
  }

  // Check if already a member
  if (group.members && group.members.includes(invitee)) {
    return; // Already a member
  }

  // Create invitation
  const invitationsRef = collection(db, 'groupInvitations');
  const invitationId = `inv_${groupId}_${invitee}_${Date.now()}`;
  const invitationRef = doc(invitationsRef, invitationId);

  await setDoc(invitationRef, {
    groupId,
    inviter,
    invitee,
    status: 'pending', // 'pending', 'accepted', 'rejected'
    createdAt: serverTimestamp(),
    expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) // 30 days
  });
};

/**
 * Accept group invitation
 * @param {string} invitationId - Invitation ID
 * @param {string} userAddress - Wallet address of user accepting
 * @returns {Promise<void>}
 */
export const acceptGroupInvitation = async (invitationId, userAddress) => {
  if (!invitationId || !userAddress) {
    throw new Error('Invitation ID and user address are required');
  }

  const user = normalize(userAddress);
  const invitationsRef = collection(db, 'groupInvitations');
  const invitationRef = doc(invitationsRef, invitationId);
  const invitationDoc = await getDoc(invitationRef);

  if (!invitationDoc.exists()) {
    throw new Error('Invitation not found');
  }

  const invitation = invitationDoc.data();

  if (invitation.invitee !== user) {
    throw new Error('You are not the intended recipient of this invitation');
  }

  if (invitation.status !== 'pending') {
    throw new Error('This invitation has already been processed');
  }

  // Check expiration
  if (invitation.expiresAt && invitation.expiresAt.toDate() < new Date()) {
    throw new Error('This invitation has expired');
  }

  // Join the group
  await joinGroup(invitation.groupId, user);

  // Update invitation status
  await updateDoc(invitationRef, {
    status: 'accepted',
    acceptedAt: serverTimestamp()
  });
};

/**
 * Add moderator to group
 * @param {string} groupId - Group ID
 * @param {string} creatorAddress - Wallet address of creator
 * @param {string} moderatorAddress - Wallet address of new moderator
 * @returns {Promise<void>}
 */
export const addModerator = async (groupId, creatorAddress, moderatorAddress) => {
  if (!groupId || !creatorAddress || !moderatorAddress) {
    throw new Error('Group ID, creator, and moderator addresses are required');
  }

  const creator = normalize(creatorAddress);
  const moderator = normalize(moderatorAddress);
  const group = await getGroup(groupId);

  if (!group) {
    throw new Error('Group not found');
  }

  if (group.creator !== creator) {
    throw new Error('Only the creator can add moderators');
  }

  if (!group.members || !group.members.includes(moderator)) {
    throw new Error('User must be a member before becoming a moderator');
  }

  const groupsRef = collection(db, 'groups');
  const groupRef = doc(groupsRef, groupId);

  await updateDoc(groupRef, {
    moderators: arrayUnion(moderator),
    updatedAt: serverTimestamp()
  });
};

/**
 * Remove moderator from group
 * @param {string} groupId - Group ID
 * @param {string} creatorAddress - Wallet address of creator
 * @param {string} moderatorAddress - Wallet address of moderator to remove
 * @returns {Promise<void>}
 */
export const removeModerator = async (groupId, creatorAddress, moderatorAddress) => {
  if (!groupId || !creatorAddress || !moderatorAddress) {
    throw new Error('Group ID, creator, and moderator addresses are required');
  }

  const creator = normalize(creatorAddress);
  const moderator = normalize(moderatorAddress);
  const group = await getGroup(groupId);

  if (!group) {
    throw new Error('Group not found');
  }

  if (group.creator !== creator) {
    throw new Error('Only the creator can remove moderators');
  }

  if (group.creator === moderator) {
    throw new Error('Cannot remove the creator as moderator');
  }

  const groupsRef = collection(db, 'groups');
  const groupRef = doc(groupsRef, groupId);

  await updateDoc(groupRef, {
    moderators: arrayRemove(moderator),
    updatedAt: serverTimestamp()
  });
};

/**
 * Search/discover groups by tags/interests
 * @param {Array<string>} tags - Tags to search for
 * @param {string} privacy - Privacy filter ('public', 'private', 'invite-only', or null for all)
 * @param {number} limitCount - Maximum number of results
 * @returns {Promise<Array>} Array of groups
 */
export const discoverGroups = async (tags = [], privacy = null, limitCount = 20) => {
  try {
    const groupsRef = collection(db, 'groups');
    let q = query(groupsRef);

    // Filter by privacy if specified
    if (privacy) {
      q = query(q, where('privacy', '==', privacy));
    }

    // Order by creation date (newest first)
    q = query(q, orderBy('createdAt', 'desc'));

    // Limit results
    q = query(q, limit(limitCount));

    const querySnapshot = await getDocs(q);
    const groups = [];

    querySnapshot.forEach((doc) => {
      const groupData = doc.data();
      
      // Filter by tags if provided
      if (tags.length > 0) {
        const groupTags = groupData.tags || [];
        const hasMatchingTag = tags.some(tag => 
          groupTags.some(gt => gt.toLowerCase().includes(tag.toLowerCase()) || tag.toLowerCase().includes(gt.toLowerCase()))
        );
        
        if (!hasMatchingTag) {
          return; // Skip groups without matching tags
        }
      }

      groups.push({
        id: doc.id,
        ...groupData
      });
    });

    return groups;
  } catch (error) {
    console.error('Error discovering groups:', error);
    throw error;
  }
};

/**
 * Get groups that a user is a member of
 * @param {string} userAddress - Wallet address
 * @returns {Promise<Array>} Array of groups
 */
export const getUserGroups = async (userAddress) => {
  if (!userAddress) return [];

  const user = normalize(userAddress);
  
  try {
    const groupsRef = collection(db, 'groups');
    const q = query(groupsRef, where('members', 'array-contains', user));
    const querySnapshot = await getDocs(q);
    
    const groups = [];
    querySnapshot.forEach((doc) => {
      groups.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return groups;
  } catch (error) {
    console.error('Error getting user groups:', error);
    throw error;
  }
};

/**
 * Subscribe to groups that a user is a member of
 * @param {string} userAddress - Wallet address
 * @param {Function} callback - Callback function that receives array of groups
 * @returns {Function} Unsubscribe function
 */
export const subscribeToUserGroups = (userAddress, callback) => {
  if (!userAddress) {
    callback([]);
    return () => {};
  }

  const user = normalize(userAddress);
  const groupsRef = collection(db, 'groups');
  const q = query(groupsRef, where('members', 'array-contains', user));

  const unsubscribe = onSnapshot(
    q,
    (querySnapshot) => {
      const groups = [];
      querySnapshot.forEach((doc) => {
        groups.push({
          id: doc.id,
          ...doc.data()
        });
      });
      callback(groups);
    },
    (error) => {
      console.error('Error subscribing to user groups:', error);
      callback([]);
    }
  );

  return unsubscribe;
};

/**
 * Create a post in a group
 * @param {string} groupId - Group ID
 * @param {string} authorAddress - Wallet address of post author
 * @param {Object} postData - Post data (content, images, etc.)
 * @returns {Promise<string>} Post ID
 */
export const createGroupPost = async (groupId, authorAddress, postData) => {
  if (!groupId || !authorAddress || !postData || !postData.content) {
    throw new Error('Group ID, author address, and post content are required');
  }

  // Input validation to prevent DoS attacks
  const MAX_POST_CONTENT_LENGTH = 10000; // 10KB limit
  const MAX_IMAGES = 10;

  const content = postData.content.trim();
  if (content.length === 0) {
    throw new Error('Post content cannot be empty');
  }
  if (content.length > MAX_POST_CONTENT_LENGTH) {
    throw new Error(`Post content must be no more than ${MAX_POST_CONTENT_LENGTH} characters`);
  }

  const images = postData.images || [];
  if (!Array.isArray(images)) {
    throw new Error('Images must be an array');
  }
  if (images.length > MAX_IMAGES) {
    throw new Error(`Maximum ${MAX_IMAGES} images allowed per post`);
  }

  const author = normalize(authorAddress);
  const group = await getGroup(groupId);

  if (!group) {
    throw new Error('Group not found');
  }

  // Check if user is a member
  if (!group.members || !group.members.includes(author)) {
    throw new Error('You must be a member to post in this group');
  }

  const postId = `post_${groupId}_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`; // Fixed: use substring instead of deprecated substr

  const postsRef = collection(db, 'groupPosts');
  const postRef = doc(postsRef, postId);

  const postDoc = {
    postId,
    groupId,
    author: author,
    content: content,
    images: images,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    likes: [],
    likesCount: 0,
    comments: [],
    commentsCount: 0,
    pinned: false
  };

  await setDoc(postRef, postDoc);

  // Record activity
  try {
    await recordActivity(ACTIVITY_TYPES.GROUP_POST, author, {
      groupId,
      postId,
      content: content.substring(0, 200), // Store preview
      groupName: group?.name || 'Unknown Group'
    });
  } catch (error) {
    console.warn('Error recording group post activity:', error);
  }

  return postId;
};

/**
 * Get posts for a group
 * @param {string} groupId - Group ID
 * @param {number} limitCount - Maximum number of posts to retrieve
 * @returns {Promise<Array>} Array of posts
 */
export const getGroupPosts = async (groupId, limitCount = 20) => {
  if (!groupId) return [];

  try {
    const postsRef = collection(db, 'groupPosts');
    // Don't use orderBy to avoid index issues - we'll sort client-side
    const q = query(
      postsRef,
      where('groupId', '==', groupId),
      limit(limitCount * 2) // Get more than needed since we'll sort and limit client-side
    );

    const querySnapshot = await getDocs(q);
    const posts = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      posts.push({
        id: doc.id,
        ...data
      });
    });

    // Sort client-side by createdAt (newest first)
    posts.sort((a, b) => {
      const aTime = a.createdAt?.toMillis?.() || (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0) || 0;
      const bTime = b.createdAt?.toMillis?.() || (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : 0) || 0;
      return bTime - aTime; // Descending order (newest first)
    });

    return posts.slice(0, limitCount);
  } catch (error) {
    console.error('Error getting group posts:', error);
    throw error;
  }
};

/**
 * Subscribe to posts in a group
 * @param {string} groupId - Group ID
 * @param {Function} callback - Callback function that receives array of posts
 * @returns {Function} Unsubscribe function
 */
export const subscribeToGroupPosts = (groupId, callback) => {
  if (!groupId) {
    callback([]);
    return () => {};
  }

  const postsRef = collection(db, 'groupPosts');
  // Don't use orderBy to avoid index issues - we'll sort client-side
  const q = query(
    postsRef,
    where('groupId', '==', groupId),
    limit(50)
  );

  const unsubscribe = onSnapshot(
    q,
    (querySnapshot) => {
      const posts = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        posts.push({
          id: doc.id,
          ...data
        });
      });
      
      // Sort client-side by createdAt (newest first)
      posts.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0) || 0;
        const bTime = b.createdAt?.toMillis?.() || (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : 0) || 0;
        return bTime - aTime; // Descending order (newest first)
      });
      
      callback(posts);
    },
    (error) => {
      console.error('Error subscribing to group posts:', error);
      callback([]);
    }
  );

  return unsubscribe;
};

/**
 * Like/unlike a post
 * @param {string} postId - Post ID
 * @param {string} userAddress - Wallet address of user
 * @returns {Promise<void>}
 */
export const togglePostLike = async (postId, userAddress) => {
  if (!postId || !userAddress) {
    throw new Error('Post ID and user address are required');
  }

  const user = normalize(userAddress);
  const postsRef = collection(db, 'groupPosts');
  const postRef = doc(postsRef, postId);
  const postDoc = await getDoc(postRef);

  if (!postDoc.exists()) {
    throw new Error('Post not found');
  }

  const postData = postDoc.data();
  const likes = postData.likes || [];
  const isLiked = likes.includes(user);

  await runTransaction(db, async (transaction) => {
    const currentPost = await transaction.get(postRef);
    if (!currentPost.exists()) {
      throw new Error('Post not found');
    }

    const currentData = currentPost.data();
    const currentLikes = currentData.likes || [];
    const currentlyLiked = currentLikes.includes(user);

    if (currentlyLiked) {
      transaction.update(postRef, {
        likes: arrayRemove(user),
        likesCount: increment(-1),
        updatedAt: serverTimestamp()
      });
    } else {
      transaction.update(postRef, {
        likes: arrayUnion(user),
        likesCount: increment(1),
        updatedAt: serverTimestamp()
      });
    }
  });
};

/**
 * Add comment to a post
 * @param {string} postId - Post ID
 * @param {string} authorAddress - Wallet address of comment author
 * @param {string} commentText - Comment text
 * @returns {Promise<string>} Comment ID
 */
export const addPostComment = async (postId, authorAddress, commentText) => {
  if (!postId || !authorAddress || !commentText) {
    throw new Error('Post ID, author address, and comment text are required');
  }

  // Input validation to prevent DoS attacks
  const MAX_COMMENT_LENGTH = 2000; // 2KB limit for comments

  const content = commentText.trim();
  if (content.length === 0) {
    throw new Error('Comment text cannot be empty');
  }
  if (content.length > MAX_COMMENT_LENGTH) {
    throw new Error(`Comment must be no more than ${MAX_COMMENT_LENGTH} characters`);
  }

  const author = normalize(authorAddress);
  const commentId = `comment_${postId}_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`; // Fixed: use substring instead of deprecated substr

  // Use Timestamp.fromDate() instead of serverTimestamp() because
  // serverTimestamp() cannot be used inside arrayUnion()
  // Cannot use serverTimestamp() inside arrayUnion() - use regular timestamp instead
  const comment = {
    commentId,
    author: author,
    content: content,
    createdAt: Timestamp.now()
  };

  const postsRef = collection(db, 'groupPosts');
  const postRef = doc(postsRef, postId);
  const postDoc = await getDoc(postRef);

  if (!postDoc.exists()) {
    throw new Error('Post not found');
  }

  await runTransaction(db, async (transaction) => {
    const currentPost = await transaction.get(postRef);
    if (!currentPost.exists()) {
      throw new Error('Post not found');
    }

    const currentData = currentPost.data();
    const comments = currentData.comments || [];

    transaction.update(postRef, {
      comments: arrayUnion(comment),
      commentsCount: increment(1),
      updatedAt: serverTimestamp()
    });
  });

  return commentId;
};

/**
 * Create a group event
 * @param {string} groupId - Group ID
 * @param {string} creatorAddress - Wallet address of event creator
 * @param {Object} eventData - Event data (title, description, date, location, etc.)
 * @returns {Promise<string>} Event ID
 */
export const createGroupEvent = async (groupId, creatorAddress, eventData) => {
  if (!groupId || !creatorAddress || !eventData || !eventData.title) {
    throw new Error('Group ID, creator address, and event title are required');
  }

  // Input validation to prevent DoS attacks
  const MAX_TITLE_LENGTH = 200;
  const MAX_DESCRIPTION_LENGTH = 5000;
  const MAX_LOCATION_LENGTH = 500;

  const title = eventData.title.trim();
  if (title.length === 0) {
    throw new Error('Event title cannot be empty');
  }
  if (title.length > MAX_TITLE_LENGTH) {
    throw new Error(`Event title must be no more than ${MAX_TITLE_LENGTH} characters`);
  }

  const description = (eventData.description || '').trim();
  if (description.length > MAX_DESCRIPTION_LENGTH) {
    throw new Error(`Event description must be no more than ${MAX_DESCRIPTION_LENGTH} characters`);
  }

  const location = (eventData.location || '').trim();
  if (location.length > MAX_LOCATION_LENGTH) {
    throw new Error(`Event location must be no more than ${MAX_LOCATION_LENGTH} characters`);
  }

  const creator = normalize(creatorAddress);
  const group = await getGroup(groupId);

  if (!group) {
    throw new Error('Group not found');
  }

  // Check if user is a member
  if (!group.members || !group.members.includes(creator)) {
    throw new Error('You must be a member to create events in this group');
  }

  const eventId = `event_${groupId}_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`; // Fixed: use substring instead of deprecated substr

  const eventsRef = collection(db, 'groupEvents');
  const eventRef = doc(eventsRef, eventId);

  const eventDoc = {
    eventId,
    groupId,
    creator: creator,
    title: title,
    description: description,
    startDate: eventData.startDate ? Timestamp.fromDate(new Date(eventData.startDate)) : null,
    endDate: eventData.endDate ? Timestamp.fromDate(new Date(eventData.endDate)) : null,
    location: eventData.location || null,
    attendees: [creator],
    attendeesCount: 1,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  await setDoc(eventRef, eventDoc);

  // Record activity
  try {
    await recordActivity(ACTIVITY_TYPES.GROUP_EVENT, creator, {
      groupId,
      eventId,
      title: title,
      description: description ? description.substring(0, 200) : null,
      startDate: eventData.startDate,
      location: eventData.location,
      groupName: group?.name || 'Unknown Group'
    });
  } catch (error) {
    console.warn('Error recording group event activity:', error);
  }

  return eventId;
};

/**
 * Get events for a group
 * @param {string} groupId - Group ID
 * @param {number} limitCount - Maximum number of events to retrieve
 * @returns {Promise<Array>} Array of events
 */
export const getGroupEvents = async (groupId, limitCount = 20) => {
  if (!groupId) return [];

  try {
    const eventsRef = collection(db, 'groupEvents');
    const q = query(
      eventsRef,
      where('groupId', '==', groupId),
      orderBy('startDate', 'asc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    const events = [];

    querySnapshot.forEach((doc) => {
      events.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return events;
  } catch (error) {
    console.error('Error getting group events:', error);
    throw error;
  }
};

/**
 * RSVP to a group event
 * @param {string} eventId - Event ID
 * @param {string} userAddress - Wallet address of user
 * @returns {Promise<void>}
 */
export const rsvpToEvent = async (eventId, userAddress) => {
  if (!eventId || !userAddress) {
    throw new Error('Event ID and user address are required');
  }

  const user = normalize(userAddress);
  const eventsRef = collection(db, 'groupEvents');
  const eventRef = doc(eventsRef, eventId);
  const eventDoc = await getDoc(eventRef);

  if (!eventDoc.exists()) {
    throw new Error('Event not found');
  }

  const eventData = eventDoc.data();
  const attendees = eventData.attendees || [];

  if (attendees.includes(user)) {
    return; // Already RSVP'd
  }

  await runTransaction(db, async (transaction) => {
    const currentEvent = await transaction.get(eventRef);
    if (!currentEvent.exists()) {
      throw new Error('Event not found');
    }

    const currentData = currentEvent.data();
    if (currentData.attendees && currentData.attendees.includes(user)) {
      return; // Already RSVP'd
    }

    transaction.update(eventRef, {
      attendees: arrayUnion(user),
      attendeesCount: increment(1),
      updatedAt: serverTimestamp()
    });
  });
};

/**
 * Cancel RSVP to a group event
 * @param {string} eventId - Event ID
 * @param {string} userAddress - Wallet address of user
 * @returns {Promise<void>}
 */
export const cancelRsvpToEvent = async (eventId, userAddress) => {
  if (!eventId || !userAddress) {
    throw new Error('Event ID and user address are required');
  }

  const user = normalize(userAddress);
  const eventsRef = collection(db, 'groupEvents');
  const eventRef = doc(eventsRef, eventId);

  await runTransaction(db, async (transaction) => {
    const currentEvent = await transaction.get(eventRef);
    if (!currentEvent.exists()) {
      throw new Error('Event not found');
    }

    const currentData = currentEvent.data();
    if (!currentData.attendees || !currentData.attendees.includes(user)) {
      return; // Not RSVP'd
    }

    transaction.update(eventRef, {
      attendees: arrayRemove(user),
      attendeesCount: increment(-1),
      updatedAt: serverTimestamp()
    });
  });
};

/**
 * Helper: Add user's group membership to userGroups collection
 */
const addUserGroupMembership = async (userAddress, groupId, role) => {
  try {
    const userGroupsRef = collection(db, 'userGroups');
    const userGroupRef = doc(userGroupsRef, `${userAddress}_${groupId}`);
    
    await setDoc(userGroupRef, {
      userAddress,
      groupId,
      role,
      joinedAt: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error adding user group membership:', error);
  }
};

/**
 * Helper: Remove user's group membership from userGroups collection
 */
const removeUserGroupMembership = async (userAddress, groupId) => {
  try {
    const userGroupsRef = collection(db, 'userGroups');
    const userGroupRef = doc(userGroupsRef, `${userAddress}_${groupId}`);
    await deleteDoc(userGroupRef);
  } catch (error) {
    console.error('Error removing user group membership:', error);
  }
};

/**
 * Get pending invitations for a user
 * @param {string} userAddress - Wallet address
 * @returns {Promise<Array>} Array of invitations
 */
export const getPendingInvitations = async (userAddress) => {
  if (!userAddress) return [];

  const user = normalize(userAddress);
  
  try {
    const invitationsRef = collection(db, 'groupInvitations');
    const q = query(
      invitationsRef,
      where('invitee', '==', user),
      where('status', '==', 'pending')
    );
    
    const querySnapshot = await getDocs(q);
    const invitations = [];

    querySnapshot.forEach((doc) => {
      invitations.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return invitations;
  } catch (error) {
    console.error('Error getting pending invitations:', error);
    throw error;
  }
};



